import { Chip, Divider, List, ListItem } from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import ContactEmailIcon from "@material-ui/icons/ContactMail";
import HelpIcon from "@material-ui/icons/Help";
import HowToRegIcon from "@material-ui/icons/HowToReg";
import NotificationsIcon from "@material-ui/icons/Notifications";
import StarIcon from "@material-ui/icons/Star";
import { forwardRef, useContext, useEffect, useRef } from "react";
import { AppBar, MenuItemLink, UserMenu, useTranslate } from "react-admin";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import useNZGBContext from "../../nzgb/utils/useNZGBUserContext";
import usePOCPContext from "../../pocp/utils/usePOCPUserContext";
import {
  ACS_INTERNAL_DOCUMENT_VIEWER,
  NZGB_EXTERNAL_USER,
  POCP_EXTERNAL_USER,
  REGISTERED_USER,
} from "../../shared/constants";
import { AppContext } from "../../shared/context/AppContext";
import useUserContext from "../../shared/useUserContext";
import RolesDisplay from "../../soregister/common/RolesDisplay";
import useDANDEContext from "../../soregister/utils/useDANDEUserContext";
import { AppState } from "../../types";
import Logo from "./Logo";
import LogoutWithRef from "./Logout";
import MenuItemExtLink from "./MenuItemExtLink";

const useStyles = makeStyles((theme) => ({
  title: {
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    overflow: "hidden",
    maxWidth: "33vw",
  },
  spacer: {
    flex: 1,
  },
  logoHorizontallyCenter: {
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)",
  },
  loggedInMsg: {
    marginLeft: "auto",
    fontSize: "14px",
  },
  orgSelector: {
    [theme.breakpoints.only("xl")]: {
      maxWidth: "360px",
    },
    [theme.breakpoints.only("lg")]: {
      maxWidth: "280px",
    },
    [theme.breakpoints.down("md")]: {
      maxWidth: "160px",
    },
  },
  userProfile: {},
  menu: {},
}));

const ContactTranspowerMenu = forwardRef<any, any>((props, ref) => {
  return (
    <MenuItemLink
      ref={ref}
      to="/contact"
      primaryText="Contact Transpower"
      sidebarIsOpen={true}
      leftIcon={<ContactEmailIcon />}
      onClick={props.onClick}
    />
  );
});

const UserGuideMenu = forwardRef<any, any>((props, _) => {
  const classes = useStyles();
  const translate = useTranslate();

  const internalGuide = translate(`${props.appguide}.internal`);
  const externalGuide = translate(`${props.appguide}.external`);
  const defaultGuide = translate(props.appguide);

  const userGuide =
    props.showInternalUserGuide &&
    internalGuide !== `${props.appguide}.internal`
      ? internalGuide
      : externalGuide !== `${props.appguide}.external`
      ? externalGuide
      : defaultGuide;

  return (
    <MenuItemExtLink
      to={userGuide}
      primaryText="User Guide"
      classes={classes}
      sidebarIsOpen={true}
      leftIcon={<HelpIcon />}
      target="_blank"
    />
  );
});

const RegisterUserMenu = forwardRef<any, any>((props, ref) => {
  const location = useLocation();

  return (
    <MenuItemLink
      ref={ref}
      to={{
        pathname: "/register-user",
        state: { referrer: location.pathname + location.search },
      }}
      primaryText="Register"
      sidebarIsOpen={true}
      leftIcon={<HowToRegIcon />}
      onClick={props.onClick}
    />
  );
});

const POCPNotificationMenu = forwardRef<any, any>((props, ref) => {
  const location = useLocation();

  return (
    <MenuItemLink
      ref={ref}
      to={{
        pathname: "/outages/notifications",
        state: { referrer: location.pathname + location.search },
      }}
      primaryText="Notification"
      sidebarIsOpen={true}
      leftIcon={<NotificationsIcon />}
      onClick={props.onClick}
    />
  );
});

const NZGBNotificationMenu = forwardRef<any, any>((props, ref) => {
  const location = useLocation();

  return (
    <MenuItemLink
      ref={ref}
      to={{
        pathname: "/documents/notifications",
        state: { referrer: location.pathname + location.search },
      }}
      primaryText="Notification"
      sidebarIsOpen={true}
      leftIcon={<NotificationsIcon />}
      onClick={props.onClick}
    />
  );
});

const DANDENotificationMenu = forwardRef<any, any>((props, ref) => {
  const location = useLocation();

  return (
    <MenuItemLink
      ref={ref}
      to={{
        pathname: "/notifications",
        state: { referrer: location.pathname + location.search },
      }}
      primaryText="Notification"
      sidebarIsOpen={true}
      leftIcon={<NotificationsIcon />}
      onClick={props.onClick}
    />
  );
});

const ACSNotificationMenu = forwardRef<any, any>((props, ref) => {
  const translate = useTranslate();
  return (
    <MenuItemLink
      ref={ref}
      to="/notifications"
      primaryText={translate("acs.title.manageNotifications")}
      sidebarIsOpen={true}
      leftIcon={<StarIcon />}
      onClick={props.onClick}
    />
  );
});

const DisplayName = () => {
  const translate = useTranslate();
  const userContext = useUserContext();

  if (userContext.isGuest) {
    return (
      <ListItem disableGutters={false}>
        <Typography variant="subtitle1">
          <b>{translate("portal.label.notLoggedIn")}</b>
        </Typography>
      </ListItem>
    );
  } else {
    return (
      <>
        <ListItem disableGutters={false}>
          <Typography variant="subtitle1">
            <b>{userContext?.fullname}</b>
          </Typography>
        </ListItem>
        <ListItem disableGutters={false}>
          <Typography variant="subtitle1">{userContext?.email}</Typography>
        </ListItem>
      </>
    );
  }
};

const DisplayRole = () => {
  const userContext = useUserContext();

  const translate = useTranslate();
  var roleName: string;
  var prefix: string | undefined;

  if (userContext?.isGuest) {
    roleName = "Guest";
  } else if (userContext.isInternal) {
    roleName = userContext.appPermissions[0];
  } else if (userContext.orgPermission?.permission) {
    roleName = userContext.orgPermission.permission;
    prefix = userContext.orgPermission.orgName;
  } else {
    roleName = userContext.appPermissions[0];
  }

  // never use this as the role name, find an alternative.  There should be one
  // of the other ACS internal roles present
  if (
    roleName === ACS_INTERNAL_DOCUMENT_VIEWER &&
    userContext.appPermissions.length > 1
  ) {
    roleName = userContext.appPermissions[1];
  }

  if (roleName === POCP_EXTERNAL_USER || roleName === NZGB_EXTERNAL_USER) {
    roleName = REGISTERED_USER;
  }

  if (!roleName) {
    return null;
  }

  return (
    <ListItem key={roleName}>
      <Chip
        key={roleName}
        label={`${prefix ? prefix + " - " : ""}${translate(roleName)}`}
      />
    </ListItem>
  );
};

const UserProfileMenu = forwardRef<any, any>(() => {
  const userContext = useUserContext();
  const appContext = useContext(AppContext);

  return (
    <List disablePadding={true}>
      <DisplayName />
      {userContext && appContext.appname && (
        <>
          <DisplayRole />
        </>
      )}
      {userContext && appContext.appname === "dande.name" && <RolesDisplay />}
      <Divider />
    </List>
  );
});

const CustomUserMenu = (props: any) => {
  const userContext = useUserContext();
  const pocpUserContext = usePOCPContext();
  const nzgbUserContext = useNZGBContext();
  const dandeUserContext = useDANDEContext();
  const appContext = useContext(AppContext);

  const showInternalUserGuide =
    appContext.appname === "acs.name" && userContext?.isInternal
      ? true
      : appContext.appname === "er.name" && userContext?.isInternal
      ? true
      : appContext.appname === "pocp.name" && pocpUserContext?.isSO
      ? true
      : appContext.appname === "nzgb.name" && nzgbUserContext?.isSO
      ? true
      : appContext.appname === "dande.name" && dandeUserContext?.isSO
      ? true
      : false;

  const showRegisterUserMenu =
    appContext.appname === "pocp.name" && pocpUserContext?.isGuest
      ? true
      : appContext.appname === "nzgb.name" && nzgbUserContext?.isGuest
      ? true
      : appContext.appname === "dande.name" && dandeUserContext?.isGuest
      ? true
      : false;

  const showPocpNotificationMenu =
    appContext.appname === "pocp.name" &&
    !pocpUserContext?.isGuest &&
    !userContext?.isInternalGuest
      ? true
      : false;

  const showNzgbNotificationMenu =
    appContext.appname === "nzgb.name" &&
    !nzgbUserContext?.isGuest &&
    !userContext?.isInternalGuest
      ? true
      : false;

  const showDandeNotificationMenu =
    appContext.appname === "dande.name" &&
    !dandeUserContext?.isGuest &&
    !userContext?.isInternalGuest
      ? true
      : false;

  const showAcsNotificationMenu =
    userContext?.appPermissions.some((p) => p.includes("ACS")) &&
    appContext.appname &&
    appContext.appname.includes("acs")
      ? true
      : false;

  return (
    <UserMenu {...props}>
      <UserProfileMenu />
      {!userContext?.isInternal && !userContext?.isInternalGuest && (
        <ContactTranspowerMenu />
      )}
      {showRegisterUserMenu && <RegisterUserMenu />}
      {showPocpNotificationMenu && <POCPNotificationMenu />}
      {showNzgbNotificationMenu && <NZGBNotificationMenu />}
      {showDandeNotificationMenu && <DANDENotificationMenu />}
      {appContext.appguide && (
        <UserGuideMenu
          appguide={appContext.appguide}
          showInternalUserGuide={showInternalUserGuide}
        />
      )}
      {showAcsNotificationMenu && <ACSNotificationMenu />}
    </UserMenu>
  );
};

const CustomAppBar = ({ orgPicker, ...props }) => {
  const classes = useStyles();
  const showLogo = useMediaQuery("(min-width:1000px)");
  const titleRef = useRef<HTMLDivElement>(null);
  const { title } = useSelector((state: AppState) => state.pageTitle);

  /*
   * If a title was set through <CustomTitle>, set it.
   */
  useEffect(() => {
    if (titleRef.current) {
      titleRef.current.innerHTML = title;
    }
  }, [title]);

  return (
    <AppBar
      {...props}
      logout={<LogoutWithRef button={true} />}
      elevation={1}
      userMenu={<CustomUserMenu />}
    >
      <Typography
        variant="h6"
        color="inherit"
        className={classes.title}
        ref={titleRef}
      />
      <Typography
        variant="h6"
        color="inherit"
        className={classes.title}
        id="react-admin-title"
      />
      <div className={classes.logoHorizontallyCenter}>
        {showLogo && <Logo />}
      </div>

      <span className={classes.spacer} />
      <div className={classes.orgSelector}>{orgPicker}</div>
    </AppBar>
  );
};

export default CustomAppBar;
